import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  ArrowLeftIcon,
  FunnelIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import studentAuthService from '../../../services/studentAuth';

const StudentAttendance = () => {
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    skip: 0,
    limit: 50,
  });
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek: 0,
  });

  useEffect(() => {
    loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const data = await studentAuthService.getAttendance(
        filters.skip,
        filters.limit,
        filters.fromDate || undefined,
        filters.toDate || undefined
      );
      setAttendance(data);
      calculateStats(data);
    } catch (error) {
      console.error('Failed to load attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());

    const thisMonth = data.filter(
      (record) => new Date(record.date) >= startOfMonth
    ).length;

    const thisWeek = data.filter(
      (record) => new Date(record.date) >= startOfWeek
    ).length;

    setStats({
      total: data.length,
      thisMonth,
      thisWeek,
    });
  };

  const handleApplyFilters = () => {
    loadAttendance();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      skip: 0,
      limit: 50,
    });
    setTimeout(() => loadAttendance(), 0);
  };

  const formatDate = (dateString) => {
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);

    // Get IST date string
    const istDateStr = date.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Get today's IST date string
    const now = new Date();
    const todayISTStr = now.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Get yesterday's IST date string
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayISTStr = yesterday.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    if (istDateStr === todayISTStr) {
      return 'Today';
    } else if (istDateStr === yesterdayISTStr) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
    }
  };

  const formatTime = (dateString) => {
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkOut) return null;
    const diff = new Date(checkOut) - new Date(checkIn);
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return { hours, minutes, total: Math.floor(diff / 60000) };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/student/dashboard')}
                className="p-1.5 sm:p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">My Attendance</h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">
                  Track your attendance records
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all hover:scale-105 text-sm"
            >
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all hover:scale-105">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">Total</p>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats.total}</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 hover:border-green-500/50 transition-all hover:scale-105">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">This Month</p>
            <p className="text-2xl sm:text-3xl font-bold text-green-400">{stats.thisMonth}</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 hover:border-purple-500/50 transition-all hover:scale-105">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <CalendarDaysIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">This Week</p>
            <p className="text-2xl sm:text-3xl font-bold text-purple-400">{stats.thisWeek}</p>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6 mb-4 sm:mb-6 animate-in slide-in-from-top">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                </div>
                Filter Attendance
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3 mt-4">
              <button
                onClick={handleApplyFilters}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all hover:scale-105 text-sm font-medium"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Attendance Records */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50">
          <div className="p-4 sm:p-6 border-b border-gray-700/50">
            <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              </div>
              Attendance History
            </h3>
          </div>
          {attendance.length > 0 ? (
            <div className="divide-y divide-gray-700/50">
              {attendance.map((record, index) => {
                const duration = calculateDuration(record.check_in_time, record.check_out_time);
                return (
                  <div
                    key={index}
                    className="p-4 sm:p-6 hover:bg-gray-700/30 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 sm:p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg flex-shrink-0 border border-green-500/30">
                          <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-semibold text-white mb-1">
                            {formatDate(record.date)}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span>In: {formatTime(record.check_in_time)}</span>
                            </div>
                            {record.check_out_time && (
                              <>
                                <span className="hidden sm:inline">→</span>
                                <div className="flex items-center gap-1">
                                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                  <span>Out: {formatTime(record.check_out_time)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 ml-12 sm:ml-0">
                        {duration ? (
                          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 px-3 py-2 sm:px-4 sm:py-2 rounded-lg border-2 border-blue-500/30">
                            <p className="text-xs text-blue-300 mb-0.5">Duration</p>
                            <p className="text-sm sm:text-base font-bold text-blue-400">
                              {duration.hours}h {duration.minutes}m
                            </p>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full bg-yellow-900/30 text-yellow-300 border border-yellow-700">
                            <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                            In Progress
                          </span>
                        )}
                        {record.check_out_time && (
                          <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-20">
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full p-6 sm:p-8 w-fit mx-auto mb-4 border border-green-500/20">
                <CalendarIcon className="h-12 w-12 sm:h-16 sm:w-16 text-green-400" />
              </div>
              <p className="text-gray-400 text-base sm:text-lg font-medium mb-2">No attendance records</p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Your attendance records will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
